import React, { useState, useEffect, useRef, FormEvent } from "react";
import { Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useProvinces } from "@/hooks/useProvinces";
import { useDistricts } from "@/hooks/useDistricts";
import { useCities } from "@/hooks/useCities";
import { Province, District, City } from "@/lib/types";
import { cn } from "@/lib/utils";
import { useSearch } from "@/context/SearchContext"; // Import useSearch

interface LocationSuggestion {
  id: number;
  name: string; // Display name
  slug: string; // Slug for backend
  type: "province" | "district" | "city";
}

interface PropertySearchBarProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  // No longer need onSearchSubmit prop, will use context directly
  // onSearchSubmit: (params: { searchTerm?: string; locationSlug?: string; }) => void;
}

const PropertySearchBar = ({
  searchQuery,
  setSearchQuery,
}: PropertySearchBarProps) => {
  const [suggestions, setSuggestions] = useState<LocationSuggestion[]>([]);
  const [selectedLocationSlug, setSelectedLocationSlug] = useState<
    string | undefined
  >(undefined);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);

  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  const { handleFilterChange, handleSearch } = useSearch(); // Destructure from useSearch

  const { data: provinces, isLoading: provincesLoading } = useProvinces();
  const { data: districts, isLoading: districtsLoading } = useDistricts();
  const { data: cities, isLoading: citiesLoading } = useCities();

  const allLocations: LocationSuggestion[] = React.useMemo(() => {
    const locations: LocationSuggestion[] = [];
    provinces?.forEach((p) =>
      locations.push({
        id: p.id,
        name: `${p.name} (Province)`, // Make name more specific
        slug: p.slug || "",
        type: "province",
      })
    );
    districts?.forEach((d) =>
      locations.push({
        id: d.id,
        name: `${d.name} (District)`, // Make name more specific
        slug: d.slug || "",
        type: "district",
      })
    );
    cities?.forEach((c) =>
      locations.push({
        id: c.id,
        name: `${c.display_name || c.name} (City)`, // Make name more specific
        slug: c.slug || "",
        type: "city",
      })
    );
    return locations.filter((loc) => loc.slug); // Ensure slug exists
  }, [provinces, districts, cities]);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setSuggestions([]);
      setSelectedLocationSlug(undefined);
      return;
    }

    const lowercasedQuery = searchQuery.toLowerCase();
    const filtered = allLocations.filter(
      (location) =>
        location.name.toLowerCase().includes(lowercasedQuery) ||
        location.slug.toLowerCase().includes(lowercasedQuery)
    );
    setSuggestions(filtered);
    setActiveSuggestionIndex(-1); // Reset active suggestion on new query
  }, [searchQuery, allLocations]);

  const handleSelectSuggestion = (suggestion: LocationSuggestion) => {
    setSearchQuery(suggestion.name);
    setSelectedLocationSlug(suggestion.slug);
    setShowSuggestions(false);
    setSuggestions([]); // Clear suggestions after selection
    inputRef.current?.focus(); // Keep focus on input
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (selectedLocationSlug) {
      handleFilterChange({ locations: [selectedLocationSlug] });
    } else if (searchQuery.trim()) {
      handleSearch(e); // Use the existing handleSearch for search_term
    } else {
      // If nothing is entered, clear both location and search_term
      handleFilterChange({ locations: [] });
      handleSearch(e); // This will clear search_term if searchQuery is empty
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveSuggestionIndex((prevIndex) =>
        Math.min(prevIndex + 1, suggestions.length - 1)
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveSuggestionIndex((prevIndex) => Math.max(prevIndex - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeSuggestionIndex > -1 && suggestions[activeSuggestionIndex]) {
        handleSelectSuggestion(suggestions[activeSuggestionIndex]);
      } else {
        onSubmit(e); // Submit form if no suggestion is active
      }
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
      setActiveSuggestionIndex(-1);
    }
  };

  const handleInputFocus = () => {
    setShowSuggestions(true);
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setTimeout(() => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(e.relatedTarget as Node)
      ) {
        setShowSuggestions(false);
      }
    }, 100);
  };

  return (
    <form onSubmit={onSubmit} className="relative max-w-md w-full">
      <Input
        ref={inputRef}
        type="text"
        id="property-search"
        name="property-search"
        placeholder="Province, Districts, or Cities"
        value={searchQuery}
        onChange={(e) => {
          setSearchQuery(e.target.value);
          setSelectedLocationSlug(undefined); // Clear selected slug on new input
        }}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        onKeyDown={handleKeyDown}
        className="input-search h-12 pl-12 w-full rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
      />
      <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">
        <Search size={18} />
      </div>
      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="absolute z-10 w-full bg-white border border-gray-200 rounded-md shadow-lg mt-1 max-h-60 overflow-y-auto"
        >
          {suggestions.map((suggestion, index) => (
            <div
              key={`${suggestion.type}-${suggestion.id}`}
              className={cn(
                "px-4 py-2 cursor-pointer hover:bg-gray-100",
                activeSuggestionIndex === index && "bg-gray-100"
              )}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleSelectSuggestion(suggestion)}
            >
              {suggestion.name}{" "}
              <span className="text-gray-500 text-sm">({suggestion.type})</span>
            </div>
          ))}
        </div>
      )}
      <Button
        type="submit"
        className="absolute right-1 top-1/2 transform -translate-y-1/2 h-10 bg-propertylk-blue hover:bg-propertylk-darkblue transition-colors"
      >
        Search
      </Button>
    </form>
  );
};

export default PropertySearchBar;
